/** Pure gate for the "Sophia Makes the Call" action in Follow Up Now.
 * Kept separate from canonicalCallGate (the existing "I'll Make the Call"
 * human-softphone gate, unchanged) because Sophia has an additional hard
 * requirement: a verified, authorized communication identity must be
 * selected. Never derives a speaker identity from the logged-in user --
 * `identity` must come from an explicit selection against
 * GET /api/communication-identities. */
export function isUsableIdentity(identity) {
  return Boolean(identity && identity.id && identity.verified_at && identity.active !== false);
}

/** The baseline checks that don't depend on an identity being selected yet --
 * used to decide whether the "Sophia Makes the Call" preflight panel may even
 * be opened. Selecting a communication identity happens inside that panel, so
 * this must not require one (that would make the panel impossible to open). */
export function canonicalSophiaOpenGate(facts, role) {
  const roleAllowed = ["admin", "ops_manager", "agent", "bpo_closer"].includes(role);
  const contactId = facts?.lead_id || facts?.client_id || "";
  const reasons = [];
  if (!roleAllowed) {
    reasons.push("Sophia calling is available to administrators, licensed agents, and assigned BPO callers.");
  }
  if (!contactId) {
    reasons.push("No linked contact could be resolved for this follow-up.");
  }
  if (facts?.callable !== true) {
    reasons.push(facts?.missing_reason || "This linked contact is not callable.");
  }
  return { allowed: reasons.length === 0, reasons, contactId };
}

/** The full gate, including the identity requirement -- used to enable the
 * final "Confirm -- Sophia Calls Now" button. */
export function canonicalSophiaCallGate(facts, role, identity) {
  const base = canonicalSophiaOpenGate(facts, role);
  const reasons = [...base.reasons];
  if (!isUsableIdentity(identity)) {
    reasons.push("Select a verified communication identity before Sophia can call.");
  }
  return { allowed: reasons.length === 0, reasons, contactId: base.contactId };
}
